import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AIAnalysisPanel } from "@/components/AIAnalysisPanel";
import { ReconcilePanel } from "@/components/ReconcilePanel";
import { CategoryRulesCard } from "@/components/CategoryRulesCard";
import { SuggestedRulesCard } from "@/components/SuggestedRulesCard";
import { MonthSignOff } from "@/components/MonthSignOff";
import { ReceiptsDialog } from "@/components/ReceiptsDialog";
import { MemoInput } from "@/components/MemoInput";
import { ImportReview } from "@/components/ImportReview";
import { UnmatchedReceipts } from "@/components/UnmatchedReceipts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Edit, DollarSign, TrendingUp, TrendingDown,
  Wallet, BarChart3, ArrowUpRight, ArrowDownRight, Clock,
  CalendarClock, Flame, Banknote, Upload, FileSpreadsheet, Loader2, Check, X, AlertCircle,
  Filter, Search, ChevronDown, ChevronUp, Sparkles, AlertTriangle, CheckCircle2, Info, RefreshCw,
  Download, LayoutList, Scan,
  GitCompare, Lock, ShieldCheck, Paperclip, Inbox,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Transaction types with direction
const TRANSACTION_TYPES = {
  income: { label: "Income", direction: "positive", group: "standard" },
  expense: { label: "Expense", direction: "negative", group: "standard" },
  investment: { label: "Investment", direction: "positive", group: "funding" },
  investment_out: { label: "Investment Out", direction: "negative", group: "funding" },
  loan: { label: "Loan Received", direction: "positive", group: "funding" },
  loan_repayment: { label: "Loan Repayment", direction: "negative", group: "funding" },
  directors_loan_in: { label: "Directors Loan (In)", direction: "positive", group: "directors" },
  directors_loan_out: { label: "Directors Loan (Out)", direction: "negative", group: "directors" },
  dividends: { label: "Dividends", direction: "negative", group: "directors" },
  refund: { label: "Refund Received", direction: "positive", group: "other" },
  refund_given: { label: "Refund Given", direction: "negative", group: "other" },
};

const TYPE_GROUPS = [
  { key: "standard", label: "Standard" },
  { key: "funding", label: "Investment & Loans" },
  { key: "directors", label: "Directors" },
  { key: "other", label: "Other" },
];

const emptyTxn = { store_id: "", type: "expense", category: "rent", amount: "", description: "", date: "", payment_status: "paid", reference: "", vendor: "" };
const emptyFP = { store_id: "", type: "expense", category: "rent", amount: "", description: "", due_date: "", recurring: "none", end_date: "", vendor: "" };

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AccountsView({ embedded = false }) {
  const [tab, setTab] = useState("transactions");
  const [transactions, setTransactions] = useState([]);
  const [futurePayments, setFuturePayments] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [burnRate, setBurnRate] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStore, setFilterStore] = useState("all");
  const [forecastMonths, setForecastMonths] = useState("3");
  const [cashflowMonths, setCashflowMonths] = useState("3");
  
  // P&L date range (default: current calendar year)
  const [pnlDateFrom, setPnlDateFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [pnlDateTo, setPnlDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  
  // Cashflow date range
  const [cfMode, setCfMode] = useState("months"); // "months" or "range"
  const [cfDateFrom, setCfDateFrom] = useState("");
  const [cfDateTo, setCfDateTo] = useState("");
  
  // Transaction filters
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterVendor, setFilterVendor] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, income, expense
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterAmountMin, setFilterAmountMin] = useState("");
  const [filterAmountMax, setFilterAmountMax] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingFinancials, setAnalyzingFinancials] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(true);

  // Categories state
  const [categories, setCategories] = useState({ positive: [], negative: [], custom: [] });
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const [newCatDirection, setNewCatDirection] = useState("negative");

  // Dialog state
  const [txnDialog, setTxnDialog] = useState(false);
  const [fpDialog, setFpDialog] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [editingFP, setEditingFP] = useState(null);
  const [txnForm, setTxnForm] = useState(emptyTxn);
  const [fpForm, setFpForm] = useState(emptyFP);
  
  // Excel Import state
  const [importDialog, setImportDialog] = useState(false);
  const [importStep, setImportStep] = useState("upload"); // upload, mapping, importing, done
  const [importFile, setImportFile] = useState(null);
  const [importAnalysis, setImportAnalysis] = useState(null);
  const [importMapping, setImportMapping] = useState({});
  const [importMode, setImportMode] = useState("add");
  const [reviewRows, setReviewRows] = useState([]);
  const [defaultType, setDefaultType] = useState("expense");
  const [defaultCategory, setDefaultCategory] = useState("other_expense");
  const [defaultStoreId, setDefaultStoreId] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // AI Analysis state
  const [categoryRules, setCategoryRules] = useState([]);
  const [receiptTxn, setReceiptTxn] = useState(null);

  // Providers state
  const [providers, setProviders] = useState([]);
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorSuggestions, setShowVendorSuggestions] = useState(false);
  const [fpVendorSuggestions, setFpVendorSuggestions] = useState([]);
  const [showFpVendorSuggestions, setShowFpVendorSuggestions] = useState(false);
  const vendorRef = useRef(null);
  const fpVendorRef = useRef(null);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchTransactions(); }, [filterStore]);
  useEffect(() => { fetchPnl(); }, [filterStore, pnlDateFrom, pnlDateTo]);
  useEffect(() => { fetchCashflow(); }, [filterStore, cashflowMonths, cfMode, cfDateFrom, cfDateTo]);
  useEffect(() => { fetchForecast(); }, [filterStore, forecastMonths]);
  useEffect(() => { fetchBurnRate(); }, []);

  const fetchAll = async () => {
    try {
      const [tRes, fpRes, sRes, catRes, provRes] = await Promise.all([
        axios.get(`${API}/transactions`), axios.get(`${API}/future-payments`), axios.get(`${API}/stores`), axios.get(`${API}/accounts/categories`),
        axios.get(`${API}/providers`),
      ]);
      setTransactions(tRes.data || []); 
      setFuturePayments(fpRes.data || []); 
      setStores(sRes.data || []); 
      setCategories(catRes.data || { positive: [], negative: [], custom: [] });
      setProviders(provRes.data || []);
      fetchRules();
    } catch (e) { console.error("fetchAll error:", e); }
    finally { setLoading(false); }
  };

  const fetchRules = async () => {
    try { setCategoryRules((await axios.get(`${API}/category-rules`)).data || []); } catch (e) { console.error(e); }
  };

  const ruleAppliedFor = useRef("");
  useEffect(() => {
    if (!txnDialog || editingTxn) return;
    const key = `${txnForm.type}|${(txnForm.vendor || "").trim().toLowerCase()}`;
    if (key === ruleAppliedFor.current) return;
    const rule = categoryRules.find(r => r.vendor.toLowerCase() === (txnForm.vendor || "").trim().toLowerCase() && r.type === txnForm.type);
    if (!rule) return;
    ruleAppliedFor.current = key;
    if (rule.category !== txnForm.category) {
      setTxnForm(f => ({ ...f, category: rule.category }));
      toast.info(`Category set by rule for ${rule.vendor}`);
    }
  }, [txnForm.vendor, txnForm.type, txnDialog, editingTxn, categoryRules]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/accounts/categories`);
      setCategories(res.data || { positive: [], negative: [], custom: [] });
    } catch (e) { console.error("fetchCategories error:", e); }
  };

  const fetchTransactions = async () => {
    try {
      const params = {};
      if (filterStore !== "all") params.store_id = filterStore;
      setTransactions((await axios.get(`${API}/transactions`, { params })).data);
    } catch (e) { console.error(e); }
  };

  const fetchPnl = async () => {
    try {
      const params = {};
      if (filterStore !== "all") params.store_id = filterStore;
      if (pnlDateFrom) params.start_date = pnlDateFrom;
      if (pnlDateTo) params.end_date = pnlDateTo;
      setPnl((await axios.get(`${API}/accounts/pnl`, { params })).data);
    } catch (e) { console.error(e); }
  };

  const fetchCashflow = async () => {
    try {
      const params = {};
      if (filterStore !== "all") params.store_id = filterStore;
      if (cfMode === "range" && cfDateFrom && cfDateTo) {
        params.start_date = cfDateFrom;
        params.end_date = cfDateTo;
      } else {
        params.months = cashflowMonths;
      }
      setCashflow((await axios.get(`${API}/accounts/cashflow`, { params })).data);
    } catch (e) { console.error(e); }
  };

  const fetchForecast = async () => {
    try {
      const params = { months: forecastMonths };
      if (filterStore !== "all") params.store_id = filterStore;
      setForecast((await axios.get(`${API}/accounts/forecast`, { params })).data);
    } catch (e) { console.error(e); }
  };

  const fetchBurnRate = async () => {
    try { setBurnRate((await axios.get(`${API}/accounts/burn-rate`)).data); } catch (e) { console.error(e); }
  };

  const refreshProviders = async () => {
    try { setProviders((await axios.get(`${API}/providers`)).data || []); } catch (e) {}
  };

  const filterVendorSuggestions = (query) => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const txnVendors = transactions.map(t => t.vendor).filter(Boolean);
    const allNames = [...new Set([
      ...providers.map(p => p.name),
      ...txnVendors
    ])];
    return allNames.filter(n => n.toLowerCase().includes(q) && n.toLowerCase() !== q.toLowerCase()).slice(0, 8);
  };

  const handleTxnSubmit = async (e) => {
    e.preventDefault();
    if (!txnForm.amount || !txnForm.date) { toast.error("Amount and date required"); return; }
    const payload = { ...txnForm, store_id: txnForm.store_id || null, amount: parseFloat(txnForm.amount) };
    try {
      if (editingTxn) { await axios.put(`${API}/transactions/${editingTxn.id}`, payload); toast.success("Updated"); }
      else { await axios.post(`${API}/transactions`, payload); toast.success("Transaction added"); }
      setTxnDialog(false); setTxnForm(emptyTxn); setEditingTxn(null); setShowVendorSuggestions(false);
      fetchTransactions(); fetchPnl(); fetchCashflow(); fetchForecast(); fetchBurnRate(); refreshProviders();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteTxn = async (id) => {
    try { await axios.delete(`${API}/transactions/${id}`); toast.success("Deleted"); fetchTransactions(); fetchPnl(); fetchCashflow(); fetchForecast(); fetchBurnRate(); }
    catch (e) { toast.error("Failed"); }
  };

  const handleFPSubmit = async (e) => {
    e.preventDefault();
    if (!fpForm.amount || !fpForm.due_date) { toast.error("Amount and due date required"); return; }
    const payload = { ...fpForm, store_id: fpForm.store_id || null, amount: parseFloat(fpForm.amount), end_date: fpForm.end_date || null };
    try {
      if (editingFP) { await axios.put(`${API}/future-payments/${editingFP.id}`, payload); toast.success("Updated"); }
      else { await axios.post(`${API}/future-payments`, payload); toast.success("Payment scheduled"); }
      setFpDialog(false); setFpForm(emptyFP); setEditingFP(null); setShowFpVendorSuggestions(false);
      setFuturePayments((await axios.get(`${API}/future-payments`)).data); fetchForecast(); refreshProviders();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteFP = async (id) => {
    try { await axios.delete(`${API}/future-payments/${id}`); toast.success("Deleted");
      setFuturePayments((await axios.get(`${API}/future-payments`)).data); fetchForecast();
    } catch (e) { toast.error("Failed"); }
  };

  const markFPPaid = async (id) => {
    try {
      await axios.post(`${API}/future-payments/${id}/mark-paid`);
      toast.success("Marked as paid — moved to transactions");
      setFuturePayments((await axios.get(`${API}/future-payments`)).data);
      fetchTransactions(); fetchPnl(); fetchCashflow(); fetchForecast(); fetchBurnRate();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark as paid"); }
  };

  // Excel Import Functions
  const resetImport = () => {
    setImportStep("upload");
    setImportFile(null);
    setImportAnalysis(null);
    setImportMapping({});
    setImportMode("add");
    setDefaultType("expense");
    setDefaultCategory("other_expense");
    setDefaultStoreId("");
    setImportResult(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      setImportStep("analyzing");
      const res = await axios.post(`${API}/accounts/import/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setImportAnalysis(res.data);
      setImportMapping(res.data.auto_mapping || {});
      setImportStep("mapping");
    } catch (err) {
      console.error("Import analyze error:", err);
      toast.error(err.response?.data?.detail || err.message || "Failed to analyze file");
      setImportStep("upload");
      setImportFile(null);
    }
  };

  const executeImport = async () => {
    if (!importFile) return;
    setImporting(true);
    
    const formData = new FormData();
    formData.append("file", importFile);
    formData.append("column_mapping", JSON.stringify(importMapping));
    formData.append("import_mode", importMode);
    formData.append("default_type", defaultType);
    formData.append("default_category", defaultCategory);
    formData.append("default_store_id", defaultStoreId);
    formData.append("preview", "true");
    
    try {
      const res = await axios.post(`${API}/accounts/import/execute`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (!res.data.rows.length) { toast.error("No valid rows found in the file"); return; }
      setReviewRows(res.data.rows);
      setImportStep("review");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read file");
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const rows = reviewRows.filter(r => !r.locked).map(({ suggested, suggestion_source, category_from_rule, locked, row, ...r }) => r);
      const res = await axios.post(`${API}/accounts/import/rows`, { rows, import_mode: importMode, file_name: importFile?.name || "" });
      setImportResult({ ...res.data, skipped_locked: reviewRows.filter(r => r.locked).length });
      setImportStep("done");
      fetchAll();
      fetchPnl();
      fetchCashflow();
      fetchForecast();
      fetchBurnRate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Category Management
  const addCategory = async () => {
    if (!newCatLabel.trim()) { toast.error("Category name is required"); return; }
    try {
      await axios.post(`${API}/accounts/categories`, { label: newCatLabel, direction: newCatDirection });
      toast.success("Category added");
      setNewCatLabel("");
      fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add category"); }
  };

  // Category deletion with usage check
  const [catDeleteDialog, setCatDeleteDialog] = useState({ open: false, value: null, label: "", usage: null, loading: false });

  const startDeleteCategory = async (catValue, catLabel) => {
    setCatDeleteDialog({ open: true, value: catValue, label: catLabel, usage: null, loading: true });
    try {
      const res = await axios.get(`${API}/accounts/categories/${catValue}/usage`);
      setCatDeleteDialog(prev => ({ ...prev, usage: res.data, loading: false }));
    } catch {
      setCatDeleteDialog(prev => ({ ...prev, usage: { total: 0 }, loading: false }));
    }
  };

  const confirmDeleteCategory = async () => {
    const { value } = catDeleteDialog;
    setCatDeleteDialog(prev => ({ ...prev, loading: true }));
    try {
      await axios.delete(`${API}/accounts/categories/by-value/${value}`);
      toast.success("Category deleted");
      setCatDeleteDialog({ open: false, value: null, label: "", usage: null, loading: false });
      fetchCategories();
      fetchTransactions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete category");
      setCatDeleteDialog(prev => ({ ...prev, loading: false }));
    }
  };


  // AI Analysis function
  const analyzeFinancials = async () => {
    if (transactions.length === 0) {
      toast.error("No transactions to analyze");
      return;
    }
    setAnalyzingFinancials(true);
    try {
      const res = await axios.post(`${API}/accounts/analyze`);
      setAiAnalysis(res.data);
    } catch (e) {
      toast.error("Failed to analyze financials");
      console.error(e);
    } finally {
      setAnalyzingFinancials(false);
    }
  };

  // Helper functions - MUST be defined before computeChartData
  const getStoreName = (id) => stores.find(s => s.id === id)?.name || "General";
  const getTypeInfo = (type) => TRANSACTION_TYPES[type] || { label: type, direction: "negative" };
  const isPositiveType = (type) => getTypeInfo(type).direction === "positive";
  const _txn_is_inflow_fe = (type) => isPositiveType(type);
  
  // Get categories based on transaction type's direction
  const getCategoriesForType = (type) => {
    const dir = isPositiveType(type) ? "positive" : "negative";
    return categories[dir] || [];
  };
  
  const catLabel = (cat) => {
    const allCats = [...(categories.positive || []), ...(categories.negative || [])];
    return allCats.find(c => c.value === cat)?.label || cat;
  };

  // Compute chart data from transactions
  const computeChartData = () => {
    if (transactions.length === 0) return { monthly: [], categoryBreakdown: [], topExpenses: [] };
    
    // Group by month
    const monthlyData = {};
    transactions.forEach(t => {
      const month = t.date?.substring(0, 7) || "Unknown";
      if (!monthlyData[month]) {
        monthlyData[month] = { month, income: 0, expenses: 0 };
      }
      if (isPositiveType(t.type)) {
        monthlyData[month].income += t.amount;
      } else {
        monthlyData[month].expenses += t.amount;
      }
    });
    const monthly = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month)).slice(-6);
    
    // Category breakdown for expenses
    const catData = {};
    transactions.filter(t => !isPositiveType(t.type)).forEach(t => {
      const cat = catLabel(t.category);
      catData[cat] = (catData[cat] || 0) + t.amount;
    });
    const categoryBreakdown = Object.entries(catData)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
    
    // Top expenses by vendor
    const vendorData = {};
    transactions.filter(t => !isPositiveType(t.type) && t.vendor).forEach(t => {
      vendorData[t.vendor] = (vendorData[t.vendor] || 0) + t.amount;
    });
    const topExpenses = Object.entries(vendorData)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
    
    return { monthly, categoryBreakdown, topExpenses };
  };

  const chartData = computeChartData();
  const CHART_COLORS = ['#059669', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#ca8a04'];

  const cats = getCategoriesForType(txnForm.type);
  const fpCats = getCategoriesForType(fpForm.type);

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    // Type filter
    if (filterType !== "all") {
      if (filterType === "income" && !isPositiveType(t.type)) return false;
      if (filterType === "expense" && isPositiveType(t.type)) return false;
    }
    // Category filter
    if (filterCategory !== "all" && t.category !== filterCategory) return false;
    // Vendor filter
    if (filterVendor && !(t.vendor || "").toLowerCase().includes(filterVendor.toLowerCase())) return false;
    // Date range filter
    if (filterDateFrom && t.date < filterDateFrom) return false;
    if (filterDateTo && t.date > filterDateTo) return false;
    // Amount range filter
    if (filterAmountMin && t.amount < parseFloat(filterAmountMin)) return false;
    if (filterAmountMax && t.amount > parseFloat(filterAmountMax)) return false;
    return true;
  });

  // Get unique vendors for dropdown
  const uniqueVendors = [...new Set(transactions.map(t => t.vendor).filter(Boolean))].sort();

  // Get all categories for filter dropdown
  const allCategories = [...(categories.positive || []), ...(categories.negative || [])];

  // Count active filters
  const activeFilterCount = [
    filterType !== "all",
    filterCategory !== "all",
    filterVendor,
    filterDateFrom,
    filterDateTo,
    filterAmountMin,
    filterAmountMax
  ].filter(Boolean).length;

  // Clear all filters
  const clearFilters = () => {
    setFilterType("all");
    setFilterCategory("all");
    setFilterVendor("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterAmountMin("");
    setFilterAmountMax("");
  };

  // Export filtered transactions to CSV
  const exportCSV = () => {
    const rows = filteredTransactions.map(t => {
      const storeName = stores.find(s => s.id === t.store_id)?.name || "";
      return {
        Date: t.date || "",
        Type: t.type || "",
        Category: t.category || "",
        Description: t.description || "",
        Amount: t.amount || 0,
        Vendor: t.vendor || "",
        Store: storeName,
        Status: t.payment_status || "",
        Reference: t.reference || "",
      };
    });

    if (rows.length === 0) { toast.info("No transactions to export"); return; }

    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(","),
      ...rows.map(r => headers.map(h => {
        let val = String(r[h] ?? "");
        if (val.includes(",") || val.includes('"') || val.includes("\n")) {
          val = '"' + val.replace(/"/g, '""') + '"';
        }
        return val;
      }).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const dateSuffix = new Date().toISOString().slice(0, 10);
    const filterSuffix = activeFilterCount > 0 ? "_filtered" : "";
    a.download = `transactions${filterSuffix}_${dateSuffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} transactions`);
  };

  const TABS = [
    { id: "transactions", label: "Transactions", icon: Banknote },
    { id: "breakdown", label: "Breakdown", icon: LayoutList },
    { id: "ai-analysis", label: "AI Analysis", icon: Scan },
    { id: "reconcile", label: "Reconcile", icon: GitCompare },
    { id: "signoff", label: "Sign-Off", icon: ShieldCheck },
    { id: "receipts", label: "Receipts Inbox", icon: Inbox },
    { id: "future", label: "Future Payments", icon: CalendarClock },
    { id: "pnl", label: "P&L", icon: BarChart3 },
    { id: "cashflow", label: "Cash Flow", icon: TrendingUp },
    { id: "forecast", label: "Cash Forecast", icon: Wallet },
    { id: "burn", label: "Burn Rate", icon: Flame },
    { id: "categories", label: "Categories", icon: Plus },
  ];

  return (
    <div className={embedded ? "" : "min-h-screen px-6 md:px-12 lg:px-16 py-10"} data-testid="accounts-page">
      {!embedded && (
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-stone-50" />
              </div>
              <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Financial Management</span>
            </div>
            <h1 data-testid="accounts-title" className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>Accounts</h1>
            <p className="text-base text-stone-500 mt-2">P&L, cash flow, burn rate, and cash position forecasting.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0 mb-6">
        <div className="flex gap-1 bg-stone-100 p-1 rounded-lg w-fit min-w-max">
          {TABS.map(t => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-xs font-medium rounded-md transition-all whitespace-nowrap flex items-center gap-1.5 ${tab === t.id ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Store filter */}
      {!["signoff", "reconcile", "ai-analysis", "receipts"].includes(tab) && <div className="flex items-center gap-3 mb-6">
        <Select value={filterStore} onValueChange={setFilterStore}>
          <SelectTrigger data-testid="filter-acct-store" className="w-48 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stores</SelectItem>
            {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>}

      {/* ===== TRANSACTIONS ===== */}
      {tab === "transactions" && (
        <>
          {/* Visual Dashboard Section */}
          {transactions.length > 0 && (
            <div className="mb-6 space-y-4">
              {/* AI Analysis Card */}
              <Card className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200/60 rounded-xl">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-violet-600" />
                      </div>
                      <h3 className="font-semibold text-stone-900">AI Financial Insights</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        data-testid="analyze-financials-btn"
                        variant="outline"
                        size="sm"
                        onClick={analyzeFinancials}
                        disabled={analyzingFinancials}
                        className="bg-white/80 border-violet-200"
                      >
                        {analyzingFinancials ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Analyzing...</>
                        ) : (
                          <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {aiAnalysis ? 'Refresh' : 'Analyze'}</>
                        )}
                      </Button>
                      {aiAnalysis && (
                        <Button variant="ghost" size="sm" onClick={() => setShowAnalysis(!showAnalysis)}>
                          {showAnalysis ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {aiAnalysis && showAnalysis && (
                    <div className="space-y-3">
                      {aiAnalysis.summary && (
                        <p className="text-sm text-stone-600 bg-white/60 px-3 py-2 rounded-lg">{aiAnalysis.summary}</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {aiAnalysis.insights?.map((insight, idx) => (
                          <div
                            key={idx}
                            className={`flex items-start gap-2 p-3 rounded-lg ${
                              insight.type === 'positive' ? 'bg-emerald-50 border border-emerald-200' :
                              insight.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                              insight.type === 'negative' ? 'bg-red-50 border border-red-200' :
                              'bg-white border border-stone-200'
                            }`}
                          >
                            {insight.type === 'positive' && <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />}
                            {insight.type === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />}
                            {insight.type === 'negative' && <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                            {insight.type === 'info' && <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />}
                            <div>
                              <p className="text-sm font-medium text-stone-900">{insight.title}</p>
                              <p className="text-xs text-stone-600">{insight.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!aiAnalysis && !analyzingFinancials && (
                    <p className="text-sm text-stone-500">Click "Analyze" to get AI-powered insights on your financial data.</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Income vs Expenses Trend */}
                {chartData.monthly.length > 0 && (
                  <Card className="bg-white border border-stone-200/60 rounded-xl lg:col-span-2">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-stone-900 mb-4">Income vs Expenses Trend</h3>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData.monthly}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                            <Area type="monotone" dataKey="income" stackId="1" stroke="#059669" fill="#d1fae5" name="Income" />
                            <Area type="monotone" dataKey="expenses" stackId="2" stroke="#dc2626" fill="#fee2e2" name="Expenses" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Category Breakdown */}
                {chartData.categoryBreakdown.length > 0 && (
                  <Card className="bg-white border border-stone-200/60 rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-stone-900 mb-4">Expense Categories</h3>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={chartData.categoryBreakdown}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              paddingAngle={2}
                              dataKey="value"
                            >
                              {chartData.categoryBreakdown.map((_, idx) => (
                                <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {chartData.categoryBreakdown.slice(0, 4).map((cat, idx) => (
                          <span key={cat.name} className="text-xs flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_COLORS[idx] }} />
                            {cat.name}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
              
              {/* Top Vendors */}
              {chartData.topExpenses.length > 0 && (
                <Card className="bg-white border border-stone-200/60 rounded-xl">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-3">Top Providers by Spend</h3>
                    <div className="space-y-2">
                      {chartData.topExpenses.map((vendor, idx) => {
                        const maxVal = chartData.topExpenses[0].value;
                        const pct = (vendor.value / maxVal) * 100;
                        return (
                          <div key={vendor.name} className="flex items-center gap-3">
                            <span className="text-xs text-stone-500 w-4">{idx + 1}.</span>
                            <div className="flex-1">
                              <div className="flex justify-between mb-1">
                                <span className="text-sm text-stone-700">{vendor.name}</span>
                                <span className="text-sm font-medium text-stone-900">${vendor.value.toLocaleString()}</span>
                              </div>
                              <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                <div className="h-full bg-stone-400 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Dialog open={txnDialog} onOpenChange={v => { if (!v) { setTxnDialog(false); setEditingTxn(null); setTxnForm(emptyTxn); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-txn-btn" onClick={() => { ruleAppliedFor.current = ""; setEditingTxn(null); setTxnForm({ ...emptyTxn, date: new Date().toISOString().slice(0,10) }); setTxnDialog(true); }} className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all font-medium px-5 btn-scale">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Transaction
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>{editingTxn ? "Edit" : "Add Transaction"}</DialogTitle></DialogHeader>
                <form onSubmit={handleTxnSubmit} className="space-y-3 mt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-stone-600">Type</Label>
                      <Select value={txnForm.type} onValueChange={v => {
                        const newDir = isPositiveType(v) ? "positive" : "negative";
                        const defaultCat = newDir === "positive" ? "revenue" : "rent";
                        setTxnForm({ ...txnForm, type: v, category: defaultCat });
                      }}>
                        <SelectTrigger data-testid="txn-type" className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TYPE_GROUPS.map(group => (
                            <div key={group.key}>
                              <div className="px-2 py-1 text-xs font-medium text-stone-400 uppercase">{group.label}</div>
                              {Object.entries(TRANSACTION_TYPES).filter(([_, t]) => t.group === group.key).map(([key, t]) => (
                                <SelectItem key={key} value={key}>
                                  <span className={t.direction === "positive" ? "text-emerald-600" : "text-red-600"}>
                                    {t.direction === "positive" ? "↑" : "↓"}
                                  </span> {t.label}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Category</Label>
                      <Select value={txnForm.category} onValueChange={v => setTxnForm({ ...txnForm, category: v })}>
                        <SelectTrigger data-testid="txn-category" className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{cats.map(c => <SelectItem key={c.value} value={c.value}>{c.label}{c.custom && " ✦"}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Amount ($) *</Label>
                      <Input data-testid="txn-amount" type="number" step="0.01" value={txnForm.amount} onChange={e => setTxnForm({ ...txnForm, amount: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Date *</Label>
                      <Input data-testid="txn-date" type="date" value={txnForm.date} onChange={e => setTxnForm({ ...txnForm, date: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Store</Label>
                      <Select value={txnForm.store_id || "none"} onValueChange={v => setTxnForm({ ...txnForm, store_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">General</SelectItem>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Status</Label>
                      <Select value={txnForm.payment_status} onValueChange={v => setTxnForm({ ...txnForm, payment_status: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="paid">Paid</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="overdue">Overdue</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600">Description</Label>
                    <div className="mt-1"><MemoInput testId="txn-desc" value={txnForm.description} vendor={txnForm.vendor} onChange={v => setTxnForm(f => ({ ...f, description: v }))} placeholder="What is this for?" /></div>
                  </div>
                  <div className="relative" ref={vendorRef}>
                    <Label className="text-xs text-stone-600">Provider / Company</Label>
                    <Input data-testid="txn-vendor" value={txnForm.vendor}
                      onChange={e => {
                        setTxnForm({ ...txnForm, vendor: e.target.value });
                        setVendorSuggestions(filterVendorSuggestions(e.target.value));
                        setShowVendorSuggestions(true);
                      }}
                      onFocus={() => {
                        setVendorSuggestions(filterVendorSuggestions(txnForm.vendor));
                        setShowVendorSuggestions(true);
                      }}
                      onBlur={() => setTimeout(() => setShowVendorSuggestions(false), 200)}
                      placeholder="Vendor or company name" className="mt-1" autoComplete="off" />
                    {showVendorSuggestions && vendorSuggestions.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {vendorSuggestions.map(s => (
                          <button key={s} type="button"
                            onMouseDown={() => { setTxnForm({ ...txnForm, vendor: s }); setShowVendorSuggestions(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                            data-testid={`vendor-suggestion-${s}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button type="submit" data-testid="txn-submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium">{editingTxn ? "Update" : "Add"}</Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Import Excel Button */}
            <Dialog open={importDialog} onOpenChange={(v) => { if (!v) { resetImport(); } setImportDialog(v); }}>
              <DialogTrigger asChild>
                <Button data-testid="import-excel-btn" variant="outline" onClick={() => { resetImport(); setImportDialog(true); }} className="border-stone-300 text-stone-700 hover:bg-stone-50">
                  <Upload className="w-4 h-4 mr-1.5" /> Import Excel
                </Button>
              </DialogTrigger>
              <DialogContent className={`${importStep === "review" ? "sm:max-w-5xl" : "sm:max-w-2xl"} max-h-[90vh] overflow-y-auto`}>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
                    <FileSpreadsheet className="w-5 h-5 inline mr-2" />
                    Import Transactions from Excel
                  </DialogTitle>
                </DialogHeader>

                {/* Step 1: Upload */}
                {importStep === "upload" && (
                  <div className="py-8">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-stone-300 rounded-xl p-12 text-center cursor-pointer hover:border-stone-400 hover:bg-stone-50 transition-all"
                    >
                      <Upload className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-lg font-medium text-stone-700 mb-2">Drop your Excel file here</p>
                      <p className="text-sm text-stone-500 mb-4">or click to browse</p>
                      <p className="text-xs text-stone-400">Supports .xlsx, .xls, and .csv files</p>
                    </div>
                  </div>
                )}

                {/* Step: Analyzing */}
                {importStep === "analyzing" && (
                  <div className="py-12 text-center">
                    <Loader2 className="w-10 h-10 text-stone-400 animate-spin mx-auto mb-4" />
                    <p className="text-stone-600">Analyzing your file...</p>
                  </div>
                )}

                {/* Step 2: Column Mapping */}
                {importStep === "mapping" && importAnalysis && (
                  <div className="space-y-6 py-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <Check className="w-5 h-5" />
                        <span className="font-medium">File analyzed: {importAnalysis.filename}</span>
                      </div>
                      <p className="text-sm text-emerald-600 mt-1">{importAnalysis.total_rows} rows found</p>
                    </div>

                    {/* Column Mapping */}
                    <div>
                      <h3 className="text-sm font-semibold text-stone-900 mb-3">Map your columns</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { key: "date", label: "Date", required: false },
                          { key: "amount", label: "Amount", required: true },
                          { key: "type", label: "Type (Income/Expense)", required: false },
                          { key: "category", label: "Category", required: false },
                          { key: "description", label: "Description", required: false },
                          { key: "vendor", label: "Provider / Company", required: false },
                          { key: "reference", label: "Reference", required: false },
                          { key: "store", label: "Store", required: false },
                          { key: "status", label: "Payment Status", required: false },
                        ].map(field => (
                          <div key={field.key}>
                            <Label className="text-xs text-stone-600">{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
                            <Select 
                              value={importMapping[field.key] || "none"} 
                              onValueChange={v => setImportMapping({ ...importMapping, [field.key]: v === "none" ? undefined : v })}
                            >
                              <SelectTrigger className="mt-1 text-sm"><SelectValue placeholder="Select column" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— Not mapped —</SelectItem>
                                {importAnalysis.columns.map(col => (
                                  <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Defaults */}
                    <div>
                      <h3 className="text-sm font-semibold text-stone-900 mb-3">Default values (for unmapped fields)</h3>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs text-stone-600">Default Type</Label>
                          <Select value={defaultType} onValueChange={setDefaultType}>
                            <SelectTrigger className="mt-1 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="income">Income</SelectItem>
                              <SelectItem value="expense">Expense</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-stone-600">Default Category</Label>
                          <Select value={defaultCategory} onValueChange={setDefaultCategory}>
                            <SelectTrigger className="mt-1 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[...(categories.positive || []), ...(categories.negative || [])].map(c => (
                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-stone-600">Default Store</Label>
                          <Select value={defaultStoreId || "general"} onValueChange={v => setDefaultStoreId(v === "general" ? "" : v)}>
                            <SelectTrigger className="mt-1 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General (No Store)</SelectItem>
                              {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Import Mode */}
                    <div>
                      <h3 className="text-sm font-semibold text-stone-900 mb-3">Import mode</h3>
                      <div className="flex gap-3">
                        <label className={`flex-1 p-4 border rounded-lg cursor-pointer transition-all ${importMode === "add" ? "border-stone-900 bg-stone-50" : "border-stone-200"}`}>
                          <input type="radio" name="importMode" value="add" checked={importMode === "add"} onChange={() => setImportMode("add")} className="sr-only" />
                          <p className="font-medium text-stone-900">Add to existing</p>
                          <p className="text-xs text-stone-500 mt-1">Append new transactions to your current data</p>
                        </label>
                        <label className={`flex-1 p-4 border rounded-lg cursor-pointer transition-all ${importMode === "replace" ? "border-red-500 bg-red-50" : "border-stone-200"}`}>
                          <input type="radio" name="importMode" value="replace" checked={importMode === "replace"} onChange={() => setImportMode("replace")} className="sr-only" />
                          <p className="font-medium text-stone-900">Replace all</p>
                          <p className="text-xs text-red-500 mt-1">Delete all existing transactions first</p>
                        </label>
                      </div>
                    </div>

                    {/* Sample Data Preview */}
                    {importAnalysis.sample_rows?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-stone-900 mb-3">Preview (first {importAnalysis.sample_rows.length} rows)</h3>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="w-full text-xs">
                            <thead className="bg-stone-50">
                              <tr>
                                {importAnalysis.columns.slice(0, 6).map(col => (
                                  <th key={col} className="px-3 py-2 text-left font-medium text-stone-600 whitespace-nowrap">{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {importAnalysis.sample_rows.map((row, i) => (
                                <tr key={i} className="border-t border-stone-100">
                                  {importAnalysis.columns.slice(0, 6).map(col => (
                                    <td key={col} className="px-3 py-2 text-stone-700 whitespace-nowrap max-w-[150px] truncate">{String(row[col] ?? "")}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                      <Button variant="outline" onClick={() => { resetImport(); }} className="flex-1">Cancel</Button>
                      <Button 
                        onClick={executeImport} 
                        disabled={importing || !importMapping.amount}
                        className="flex-1 bg-stone-900 text-stone-50 hover:bg-stone-800"
                      >
                        {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        Review {importAnalysis.total_rows} Transactions
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Done */}
                {importStep === "review" && (
                  <ImportReview rows={reviewRows} setRows={setReviewRows} stores={stores} busy={importing}
                    categoriesFor={(type) => isPositiveType(type) ? (categories.positive || []) : (categories.negative || [])} isPositiveType={isPositiveType}
                    onBack={() => setImportStep("mapping")} onConfirm={confirmImport} />
                )}

                {importStep === "done" && importResult && (
                  <div className="py-8 text-center">
                    {importResult.created > 0 ? (
                      <>
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Check className="w-8 h-8 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-stone-900 mb-2">Import Complete!</h3>
                        <p className="text-stone-600 mb-4">
                          Successfully imported <span className="font-semibold text-emerald-600">{importResult.created}</span> transactions
                          {importResult.skipped > 0 && <span className="text-stone-400"> ({importResult.skipped} skipped)</span>}
                          {importResult.skipped_locked > 0 && <span className="text-amber-700" data-testid="import-skipped-locked"> · {importResult.skipped_locked} skipped (locked month)</span>}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <AlertCircle className="w-8 h-8 text-amber-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-stone-900 mb-2">No Transactions Imported</h3>
                        <p className="text-stone-600 mb-4">Check your column mapping and try again.</p>
                      </>
                    )}
                    {importResult.errors?.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mt-4 max-h-32 overflow-y-auto">
                        <p className="text-xs font-medium text-red-700 mb-2">Errors ({importResult.total_errors}):</p>
                        {importResult.errors.map((err, i) => (
                          <p key={i} className="text-xs text-red-600">{err}</p>
                        ))}
                      </div>
                    )}
                    <Button onClick={() => { resetImport(); setImportDialog(false); }} className="mt-6 bg-stone-900 text-stone-50 hover:bg-stone-800">
                      Done
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Filter Bar */}
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                data-testid="toggle-filters-btn"
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`${activeFilterCount > 0 ? 'border-amber-300 bg-amber-50' : ''}`}
              >
                <Filter className="w-4 h-4 mr-1.5" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
                )}
                {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
              </Button>
              
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-stone-500 hover:text-stone-700">
                  <X className="w-3.5 h-3.5 mr-1" /> Clear all
                </Button>
              )}
              
              <span className="text-sm text-stone-400 ml-auto">
                {filteredTransactions.length} of {transactions.length} transactions
              </span>
              
              <Button data-testid="export-csv-btn" variant="outline" size="sm" onClick={exportCSV}
                className="text-stone-600 hover:text-stone-800 ml-2">
                <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
              </Button>
            </div>
            
            {showFilters && (
              <div className="mt-3 p-4 bg-stone-50 rounded-xl border border-stone-200/60 space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {/* Type Filter */}
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger data-testid="filter-type" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Category Filter */}
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Category</Label>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger data-testid="filter-category" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {allCategories.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Vendor/Provider Filter */}
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Provider</Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                      <Input
                        data-testid="filter-vendor"
                        value={filterVendor}
                        onChange={e => setFilterVendor(e.target.value)}
                        placeholder="Search..."
                        className="h-9 text-sm pl-8"
                        list="vendor-list"
                      />
                      <datalist id="vendor-list">
                        {uniqueVendors.map(v => <option key={v} value={v} />)}
                      </datalist>
                    </div>
                  </div>
                  
                  {/* Date From */}
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">From Date</Label>
                    <Input
                      data-testid="filter-date-from"
                      type="date"
                      value={filterDateFrom}
                      onChange={e => setFilterDateFrom(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  {/* Date To */}
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">To Date</Label>
                    <Input
                      data-testid="filter-date-to"
                      type="date"
                      value={filterDateTo}
                      onChange={e => setFilterDateTo(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  {/* Amount Range */}
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Amount Range</Label>
                    <div className="flex gap-1 items-center">
                      <Input
                        data-testid="filter-amount-min"
                        type="number"
                        value={filterAmountMin}
                        onChange={e => setFilterAmountMin(e.target.value)}
                        placeholder="Min"
                        className="h-9 text-sm w-20"
                      />
                      <span className="text-stone-400">-</span>
                      <Input
                        data-testid="filter-amount-max"
                        type="number"
                        value={filterAmountMax}
                        onChange={e => setFilterAmountMax(e.target.value)}
                        placeholder="Max"
                        className="h-9 text-sm w-20"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Transactions List */}
          <div className="space-y-2">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-16">
                <Banknote className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-stone-700 mb-2">
                  {transactions.length === 0 ? "No transactions yet" : "No matching transactions"}
                </h3>
                <p className="text-sm text-stone-500">
                  {transactions.length === 0 
                    ? "Start logging income and expenses or import from Excel."
                    : "Try adjusting your filters to see more results."}
                </p>
                {activeFilterCount > 0 && (
                  <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4">
                    <X className="w-3.5 h-3.5 mr-1.5" /> Clear Filters
                  </Button>
                )}
              </div>
            ) : filteredTransactions.map((t, i) => (
              <Card key={t.id} data-testid={`txn-${t.id}`} className="bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl animate-slide-up" style={{ animationDelay: `${i * 0.02}s` }}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isPositiveType(t.type) ? "bg-emerald-50" : "bg-red-50"}`}>
                    {isPositiveType(t.type) ? <ArrowUpRight className="w-4 h-4 text-emerald-600" /> : <ArrowDownRight className="w-4 h-4 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{t.description || catLabel(t.category)}</p>
                    <p className="text-xs text-stone-400">{getTypeInfo(t.type).label} · {catLabel(t.category)}{t.vendor && ` · ${t.vendor}`} · {t.date}</p>
                  </div>
                  <span className={`text-sm font-semibold flex-shrink-0 ${isPositiveType(t.type) ? "text-emerald-700" : "text-red-600"}`}>{isPositiveType(t.type) ? "+" : "-"}{fmt(t.amount)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${t.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : t.payment_status === "overdue" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{t.payment_status}</span>
                  <Button variant="ghost" size="sm" onClick={() => setReceiptTxn(t)} className={`px-2 flex-shrink-0 ${t.receipts?.length ? "text-stone-700" : "text-stone-300 hover:text-stone-600"}`} title="Receipts" data-testid={`txn-receipts-btn-${t.id}`}>
                    <Paperclip className="w-3.5 h-3.5" />{t.receipts?.length > 0 && <span className="text-[10px] ml-0.5">{t.receipts.length}</span>}
                  </Button>
                  {t.locked ? (
                    <span className="flex items-center gap-1 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex-shrink-0" title="Month signed off" data-testid={`txn-locked-${t.id}`}><Lock className="w-3 h-3" /> Locked</span>
                  ) : <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" data-testid={`txn-edit-btn-${t.id}`} onClick={() => { setEditingTxn(t); setTxnForm({ ...t, store_id: t.store_id || "", amount: t.amount.toString(), vendor: t.vendor || "" }); setTxnDialog(true); }} className="text-stone-400 hover:text-stone-700 px-2"><Edit className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" data-testid={`txn-delete-btn-${t.id}`} onClick={() => deleteTxn(t.id)} className="text-stone-400 hover:text-red-600 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}


      {/* ===== BREAKDOWN ===== */}
      {tab === "breakdown" && (() => {
        // Use store-filtered transactions
        const txns = filterStore === "all" ? transactions : transactions.filter(t => t.store_id === filterStore);
        const totalExpense = txns.filter(t => {
          const dir = TRANSACTION_TYPES[t.type]?.direction;
          return dir === "negative";
        }).reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
        const totalIncome = txns.filter(t => {
          const dir = TRANSACTION_TYPES[t.type]?.direction;
          return dir === "positive";
        }).reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

        // Category breakdown
        const catMap = {};
        txns.forEach(t => {
          const cat = t.category || "other";
          const amt = parseFloat(t.amount) || 0;
          const dir = TRANSACTION_TYPES[t.type]?.direction;
          if (!catMap[cat]) catMap[cat] = { spend: 0, income: 0, count: 0 };
          if (dir === "negative") catMap[cat].spend += amt;
          else catMap[cat].income += amt;
          catMap[cat].count += 1;
        });
        const catRows = Object.entries(catMap)
          .map(([cat, d]) => ({ category: cat, ...d, net: d.income - d.spend }))
          .sort((a, b) => b.spend - a.spend);

        // Provider breakdown
        const provMap = {};
        txns.forEach(t => {
          const v = (t.vendor || "").trim();
          if (!v) return;
          const amt = parseFloat(t.amount) || 0;
          const dir = TRANSACTION_TYPES[t.type]?.direction;
          if (!provMap[v]) provMap[v] = { spend: 0, income: 0, count: 0 };
          if (dir === "negative") provMap[v].spend += amt;
          else provMap[v].income += amt;
          provMap[v].count += 1;
        });
        const provRows = Object.entries(provMap)
          .map(([name, d]) => ({ name, ...d, net: d.income - d.spend }))
          .sort((a, b) => b.spend - a.spend);

        const fmtAmt = (n) => `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const pct = (part, whole) => whole > 0 ? ((part / whole) * 100).toFixed(1) + "%" : "0%";
        const catLabels = {
          rent: "Rent", utilities: "Utilities", salaries: "Salaries", supplies: "Supplies",
          marketing: "Marketing", insurance: "Insurance", maintenance: "Maintenance",
          equipment: "Equipment", food_cost: "Food Cost", beverage_cost: "Beverage Cost",
          software: "Software", professional_services: "Professional Services",
          investment: "Investment", revenue: "Revenue", directors_loan_in: "Directors Loan (In)",
          directors_loan_out: "Directors Loan (Out)", other_expense: "Other Expense",
          other_income: "Other Income", loan: "Loan",
        };

        return (
          <div className="space-y-8" data-testid="breakdown-tab">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Card className="border-stone-200/60"><CardContent className="p-4">
                <p className="text-xs text-stone-500 mb-1">Total Expenses</p>
                <p className="text-xl font-bold text-stone-900">{fmtAmt(totalExpense)}</p>
              </CardContent></Card>
              <Card className="border-stone-200/60"><CardContent className="p-4">
                <p className="text-xs text-stone-500 mb-1">Total Income</p>
                <p className="text-xl font-bold text-emerald-600">{fmtAmt(totalIncome)}</p>
              </CardContent></Card>
              <Card className="border-stone-200/60"><CardContent className="p-4">
                <p className="text-xs text-stone-500 mb-1">Net</p>
                <p className={`text-xl font-bold ${totalIncome - totalExpense >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {totalIncome - totalExpense >= 0 ? "+" : "-"}{fmtAmt(Math.abs(totalIncome - totalExpense))}
                </p>
              </CardContent></Card>
            </div>

            {/* Category Breakdown */}
            <Card className="border-stone-200/60" data-testid="category-breakdown">
              <CardContent className="p-0">
                <div className="p-4 border-b border-stone-100">
                  <h3 className="text-sm font-semibold text-stone-900">Category Breakdown</h3>
                  <p className="text-xs text-stone-500 mt-0.5">{catRows.length} categories across {txns.length} transactions</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 text-xs text-stone-500">
                        <th className="text-left py-3 px-4 font-medium">Category</th>
                        <th className="text-right py-3 px-4 font-medium">Spend</th>
                        <th className="text-right py-3 px-4 font-medium">% of Spend</th>
                        <th className="text-right py-3 px-4 font-medium">Income</th>
                        <th className="text-right py-3 px-4 font-medium">% of Income</th>
                        <th className="text-right py-3 px-4 font-medium">Transactions</th>
                        <th className="py-3 px-4 font-medium w-40"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {catRows.map(r => (
                        <tr key={r.category} className="border-b border-stone-50 hover:bg-stone-50/50">
                          <td className="py-2.5 px-4 font-medium text-stone-800">{catLabels[r.category] || r.category}</td>
                          <td className="py-2.5 px-4 text-right text-stone-700">{r.spend > 0 ? fmtAmt(r.spend) : "-"}</td>
                          <td className="py-2.5 px-4 text-right text-stone-500">{r.spend > 0 ? pct(r.spend, totalExpense) : "-"}</td>
                          <td className="py-2.5 px-4 text-right text-emerald-600">{r.income > 0 ? fmtAmt(r.income) : "-"}</td>
                          <td className="py-2.5 px-4 text-right text-stone-500">{r.income > 0 ? pct(r.income, totalIncome) : "-"}</td>
                          <td className="py-2.5 px-4 text-right text-stone-500">{r.count}</td>
                          <td className="py-2.5 px-4">
                            {r.spend > 0 && (
                              <div className="bg-stone-100 rounded-full h-2 overflow-hidden">
                                <div className="bg-stone-700 h-full rounded-full" style={{ width: pct(r.spend, totalExpense) }} />
                              </div>
                            )}
                            {r.spend === 0 && r.income > 0 && (
                              <div className="bg-emerald-50 rounded-full h-2 overflow-hidden">
                                <div className="bg-emerald-500 h-full rounded-full" style={{ width: pct(r.income, totalIncome) }} />
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {catRows.length > 0 && (
                        <tr className="bg-stone-50 font-semibold text-stone-900">
                          <td className="py-2.5 px-4">Total</td>
                          <td className="py-2.5 px-4 text-right">{fmtAmt(totalExpense)}</td>
                          <td className="py-2.5 px-4 text-right">100%</td>
                          <td className="py-2.5 px-4 text-right text-emerald-600">{fmtAmt(totalIncome)}</td>
                          <td className="py-2.5 px-4 text-right">100%</td>
                          <td className="py-2.5 px-4 text-right">{txns.length}</td>
                          <td></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Provider Breakdown */}
            <Card className="border-stone-200/60" data-testid="provider-breakdown">
              <CardContent className="p-0">
                <div className="p-4 border-b border-stone-100">
                  <h3 className="text-sm font-semibold text-stone-900">Provider Breakdown</h3>
                  <p className="text-xs text-stone-500 mt-0.5">{provRows.length} providers</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 text-xs text-stone-500">
                        <th className="text-left py-3 px-4 font-medium">Provider</th>
                        <th className="text-right py-3 px-4 font-medium">Spend</th>
                        <th className="text-right py-3 px-4 font-medium">% of Spend</th>
                        <th className="text-right py-3 px-4 font-medium">Income</th>
                        <th className="text-right py-3 px-4 font-medium">% of Income</th>
                        <th className="text-right py-3 px-4 font-medium">Net Position</th>
                        <th className="text-right py-3 px-4 font-medium">Transactions</th>
                        <th className="py-3 px-4 font-medium w-40"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {provRows.map(r => (
                        <tr key={r.name} className="border-b border-stone-50 hover:bg-stone-50/50">
                          <td className="py-2.5 px-4 font-medium text-stone-800">{r.name}</td>
                          <td className="py-2.5 px-4 text-right text-stone-700">{r.spend > 0 ? fmtAmt(r.spend) : "-"}</td>
                          <td className="py-2.5 px-4 text-right text-stone-500">{r.spend > 0 ? pct(r.spend, totalExpense) : "-"}</td>
                          <td className="py-2.5 px-4 text-right text-emerald-600">{r.income > 0 ? fmtAmt(r.income) : "-"}</td>
                          <td className="py-2.5 px-4 text-right text-stone-500">{r.income > 0 ? pct(r.income, totalIncome) : "-"}</td>
                          <td className={`py-2.5 px-4 text-right font-medium ${r.net >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {r.net >= 0 ? "+" : "-"}{fmtAmt(Math.abs(r.net))}
                          </td>
                          <td className="py-2.5 px-4 text-right text-stone-500">{r.count}</td>
                          <td className="py-2.5 px-4">
                            {r.spend > 0 && (
                              <div className="bg-stone-100 rounded-full h-2 overflow-hidden">
                                <div className="bg-stone-700 h-full rounded-full" style={{ width: pct(r.spend, totalExpense) }} />
                              </div>
                            )}
                            {r.spend === 0 && r.income > 0 && (
                              <div className="bg-emerald-50 rounded-full h-2 overflow-hidden">
                                <div className="bg-emerald-500 h-full rounded-full" style={{ width: pct(r.income, totalIncome) }} />
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {provRows.length > 0 && (
                        <tr className="bg-stone-50 font-semibold text-stone-900">
                          <td className="py-2.5 px-4">Total</td>
                          <td className="py-2.5 px-4 text-right">{fmtAmt(provRows.reduce((s, r) => s + r.spend, 0))}</td>
                          <td className="py-2.5 px-4 text-right">100%</td>
                          <td className="py-2.5 px-4 text-right text-emerald-600">{fmtAmt(provRows.reduce((s, r) => s + r.income, 0))}</td>
                          <td className="py-2.5 px-4 text-right">100%</td>
                          <td className={`py-2.5 px-4 text-right ${totalIncome - totalExpense >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {totalIncome - totalExpense >= 0 ? "+" : "-"}{fmtAmt(Math.abs(totalIncome - totalExpense))}
                          </td>
                          <td className="py-2.5 px-4 text-right">{provRows.reduce((s, r) => s + r.count, 0)}</td>
                          <td></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {txns.length === 0 && (
              <div className="text-center py-16 bg-white border border-stone-200/60 rounded-xl">
                <LayoutList className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-stone-700 mb-1">No transactions to analyze</h3>
                <p className="text-sm text-stone-500">Add transactions to see your spending breakdown</p>
              </div>
            )}
          </div>
        );
      })()}



      {/* ===== AI ANALYSIS ===== */}
      <ReceiptsDialog txn={receiptTxn} onClose={() => setReceiptTxn(null)} onChanged={fetchTransactions} />

      {tab === "receipts" && <UnmatchedReceipts onChanged={fetchTransactions} />}

      {tab === "signoff" && <MonthSignOff stores={stores} onChanged={fetchTransactions} onNavigate={setTab} />}

      {tab === "reconcile" && (
        <ReconcilePanel stores={stores} categories={categories} isPositiveType={isPositiveType}
          onDataChanged={() => { fetchTransactions(); fetchPnl(); fetchCashflow(); fetchForecast(); fetchBurnRate(); refreshProviders(); }} />
      )}

      {tab === "ai-analysis" && (
        <AIAnalysisPanel totalCount={transactions.length}
          categoriesFor={(type) => isPositiveType(type) ? (categories.positive || []) : (categories.negative || [])}
          onDataChanged={() => { fetchTransactions(); fetchRules(); fetchPnl(); fetchCashflow(); refreshProviders(); }} />
      )}


      {/* ===== FUTURE PAYMENTS ===== */}
      {tab === "future" && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <Dialog open={fpDialog} onOpenChange={v => { if (!v) { setFpDialog(false); setEditingFP(null); setFpForm(emptyFP); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-fp-btn" onClick={() => { setEditingFP(null); setFpForm(emptyFP); setFpDialog(true); }} className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all font-medium px-5 btn-scale">
                  <Plus className="w-4 h-4 mr-1.5" /> Schedule Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>{editingFP ? "Edit" : "Schedule Future Payment"}</DialogTitle></DialogHeader>
                <form onSubmit={handleFPSubmit} className="space-y-3 mt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-stone-600">Type</Label>
                      <Select value={fpForm.type} onValueChange={v => {
                        const newDir = isPositiveType(v) ? "positive" : "negative";
                        const defaultCat = newDir === "positive" ? "revenue" : "rent";
                        setFpForm({ ...fpForm, type: v, category: defaultCat });
                      }}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TYPE_GROUPS.map(group => (
                            <div key={group.key}>
                              <div className="px-2 py-1 text-xs font-medium text-stone-400 uppercase">{group.label}</div>
                              {Object.entries(TRANSACTION_TYPES).filter(([_, t]) => t.group === group.key).map(([key, t]) => (
                                <SelectItem key={key} value={key}>
                                  <span className={t.direction === "positive" ? "text-emerald-600" : "text-red-600"}>
                                    {t.direction === "positive" ? "↑" : "↓"}
                                  </span> {t.label}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Category</Label>
                      <Select value={fpForm.category} onValueChange={v => setFpForm({ ...fpForm, category: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{fpCats.map(c => <SelectItem key={c.value} value={c.value}>{c.label}{c.custom && " ✦"}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Amount ($) *</Label>
                      <Input data-testid="fp-amount" type="number" step="0.01" value={fpForm.amount} onChange={e => setFpForm({ ...fpForm, amount: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Due Date *</Label>
                      <Input data-testid="fp-due" type="date" value={fpForm.due_date} onChange={e => setFpForm({ ...fpForm, due_date: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Recurring</Label>
                      <Select value={fpForm.recurring} onValueChange={v => setFpForm({ ...fpForm, recurring: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">One-time</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Store</Label>
                      <Select value={fpForm.store_id || "none"} onValueChange={v => setFpForm({ ...fpForm, store_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">General</SelectItem>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  {fpForm.recurring !== "none" && (
                    <div><Label className="text-xs text-stone-600">End Date (optional)</Label><Input type="date" value={fpForm.end_date} onChange={e => setFpForm({ ...fpForm, end_date: e.target.value })} className="mt-1" /></div>
                  )}
                  <div><Label className="text-xs text-stone-600">Description</Label><Input data-testid="fp-desc" value={fpForm.description} onChange={e => setFpForm({ ...fpForm, description: e.target.value })} placeholder="Describe this payment" className="mt-1" /></div>
                  <div className="relative" ref={fpVendorRef}>
                    <Label className="text-xs text-stone-600">Provider / Company</Label>
                    <Input data-testid="fp-vendor" value={fpForm.vendor}
                      onChange={e => {
                        setFpForm({ ...fpForm, vendor: e.target.value });
                        setFpVendorSuggestions(filterVendorSuggestions(e.target.value));
                        setShowFpVendorSuggestions(true);
                      }}
                      onFocus={() => {
                        setFpVendorSuggestions(filterVendorSuggestions(fpForm.vendor));
                        setShowFpVendorSuggestions(true);
                      }}
                      onBlur={() => setTimeout(() => setShowFpVendorSuggestions(false), 200)}
                      placeholder="Vendor or company name" className="mt-1" autoComplete="off" />
                    {showFpVendorSuggestions && fpVendorSuggestions.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {fpVendorSuggestions.map(s => (
                          <button key={s} type="button"
                            onMouseDown={() => { setFpForm({ ...fpForm, vendor: s }); setShowFpVendorSuggestions(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 transition-colors">
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button type="submit" data-testid="fp-submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium">{editingFP ? "Update" : "Schedule"}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-2">
            {futurePayments.length === 0 ? (
              <div className="text-center py-16"><CalendarClock className="w-12 h-12 text-stone-300 mx-auto mb-3" /><h3 className="text-lg font-medium text-stone-700 mb-2">No scheduled payments</h3><p className="text-sm text-stone-500">Add expected future payments to forecast your cash position.</p></div>
            ) : futurePayments.map((fp, i) => (
              <Card key={fp.id} data-testid={`fp-${fp.id}`} className="bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl animate-slide-up" style={{ animationDelay: `${i * 0.02}s` }}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${fp.type === "income" ? "bg-emerald-50" : "bg-amber-50"}`}>
                    <CalendarClock className={`w-4 h-4 ${fp.type === "income" ? "text-emerald-600" : "text-amber-600"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{fp.description || catLabel(fp.category)}</p>
                    <p className="text-xs text-stone-400">{catLabel(fp.category)}{fp.vendor && ` · ${fp.vendor}`} · Due {fp.due_date} · {fp.recurring !== "none" ? fp.recurring : "one-time"}</p>
                  </div>
                  <span className={`text-sm font-semibold flex-shrink-0 ${fp.type === "income" ? "text-emerald-700" : "text-amber-700"}`}>{fmt(fp.amount)}</span>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button data-testid={`mark-paid-${fp.id}`} variant="outline" size="sm" onClick={() => markFPPaid(fp.id)} className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200 h-8 px-2.5 text-xs font-medium"><Check className="w-3.5 h-3.5 mr-1" /> Paid</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingFP(fp); setFpForm({ ...fp, store_id: fp.store_id || "", amount: fp.amount.toString(), end_date: fp.end_date || "", vendor: fp.vendor || "" }); setFpDialog(true); }} className="text-stone-400 hover:text-stone-700 px-2"><Edit className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteFP(fp.id)} className="text-stone-400 hover:text-red-600 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* ===== P&L ===== */}
      {tab === "pnl" && (
        <div className="space-y-6">
          {/* Date Range Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-stone-500 whitespace-nowrap">From</Label>
              <Input data-testid="pnl-from" type="date" value={pnlDateFrom} onChange={e => setPnlDateFrom(e.target.value)} className="w-40 h-8 text-xs" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-stone-500 whitespace-nowrap">To</Label>
              <Input data-testid="pnl-to" type="date" value={pnlDateTo} onChange={e => setPnlDateTo(e.target.value)} className="w-40 h-8 text-xs" />
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => { setPnlDateFrom(`${new Date().getFullYear()}-01-01`); setPnlDateTo(new Date().toISOString().slice(0,10)); }} className="px-2.5 py-1 text-xs rounded-md bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors">YTD</button>
              <button onClick={() => { const y = new Date().getFullYear() - 1; setPnlDateFrom(`${y}-01-01`); setPnlDateTo(`${y}-12-31`); }} className="px-2.5 py-1 text-xs rounded-md bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors">Last Year</button>
              <button onClick={() => { setPnlDateFrom(""); setPnlDateTo(""); }} className="px-2.5 py-1 text-xs rounded-md bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors">All Time</button>
            </div>
          </div>
          
          {pnl && (
            <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-emerald-50/50 border-emerald-200/60 rounded-xl" data-testid="pnl-income"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Operating Revenue</p><p className="text-2xl font-semibold text-emerald-800">{fmt(pnl.total_income)}</p></CardContent></Card>
            <Card className="bg-red-50/50 border-red-200/60 rounded-xl" data-testid="pnl-expenses"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Operating Expenses</p><p className="text-2xl font-semibold text-red-700">{fmt(pnl.total_expenses)}</p></CardContent></Card>
            <Card className={`rounded-xl ${pnl.operating_profit >= 0 ? "bg-emerald-50/50 border-emerald-200/60" : "bg-red-50/50 border-red-200/60"}`} data-testid="pnl-operating"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Operating Profit</p><p className={`text-2xl font-semibold ${pnl.operating_profit >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmt(pnl.operating_profit)}</p></CardContent></Card>
            <Card className="bg-white border-stone-200/60 rounded-xl" data-testid="pnl-margin"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Operating Margin</p><p className="text-2xl font-semibold text-stone-900">{pnl.margin}%</p></CardContent></Card>
          </div>

          {/* P&L Statement Card */}
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-6">
            <h3 className="text-base font-semibold text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>Profit & Loss Statement</h3>
            <div className="space-y-1">
              {/* Revenue Section */}
              <div className="py-2 border-b border-stone-200">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Revenue</p>
                {Object.entries(pnl.income_by_category || {}).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                  <div key={cat} className="flex justify-between py-1.5 px-2 hover:bg-stone-50 rounded">
                    <span className="text-sm text-stone-600">{catLabel(cat)}</span>
                    <span className="text-sm font-medium text-stone-800">{fmt(amt)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 px-2 mt-1 bg-emerald-50 rounded-lg font-semibold">
                  <span className="text-sm text-emerald-800">Total Revenue</span>
                  <span className="text-sm text-emerald-800">{fmt(pnl.total_income)}</span>
                </div>
              </div>
              {/* Expenses Section */}
              <div className="py-2 border-b border-stone-200">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Operating Expenses</p>
                {Object.entries(pnl.expenses_by_category || {}).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                  <div key={cat} className="flex justify-between py-1.5 px-2 hover:bg-stone-50 rounded">
                    <span className="text-sm text-stone-600">{catLabel(cat)}</span>
                    <span className="text-sm font-medium text-stone-800">({fmt(amt)})</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 px-2 mt-1 bg-red-50 rounded-lg font-semibold">
                  <span className="text-sm text-red-800">Total Expenses</span>
                  <span className="text-sm text-red-800">({fmt(pnl.total_expenses)})</span>
                </div>
              </div>
              {/* Operating Profit */}
              <div className={`flex justify-between py-3 px-3 rounded-lg font-bold text-base ${pnl.operating_profit >= 0 ? "bg-emerald-100/70" : "bg-red-100/70"}`}>
                <span className={pnl.operating_profit >= 0 ? "text-emerald-900" : "text-red-900"}>Operating Profit</span>
                <span className={pnl.operating_profit >= 0 ? "text-emerald-900" : "text-red-900"}>{fmt(pnl.operating_profit)}</span>
              </div>
              {/* Other Activities */}
              {(pnl.other?.total_in > 0 || pnl.other?.total_out > 0) && (
                <div className="py-2 border-b border-stone-200">
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2 mt-2">Other Income / Expenses</p>
                  {Object.entries(pnl.other?.inflows || {}).map(([cat, amt]) => (
                    <div key={cat} className="flex justify-between py-1.5 px-2"><span className="text-sm text-stone-600">{catLabel(cat)}</span><span className="text-sm font-medium text-emerald-700">{fmt(amt)}</span></div>
                  ))}
                  {Object.entries(pnl.other?.outflows || {}).map(([cat, amt]) => (
                    <div key={cat} className="flex justify-between py-1.5 px-2"><span className="text-sm text-stone-600">{catLabel(cat)}</span><span className="text-sm font-medium text-red-600">({fmt(amt)})</span></div>
                  ))}
                </div>
              )}
              {/* Net Profit (if different from operating) */}
              {pnl.net_profit !== pnl.operating_profit && (
                <div className={`flex justify-between py-3 px-3 rounded-lg font-bold text-base mt-1 ${pnl.net_profit >= 0 ? "bg-emerald-100/70" : "bg-red-100/70"}`}>
                  <span className={pnl.net_profit >= 0 ? "text-emerald-900" : "text-red-900"}>Net Profit</span>
                  <span className={pnl.net_profit >= 0 ? "text-emerald-900" : "text-red-900"}>{fmt(pnl.net_profit)}</span>
                </div>
              )}
            </div>
          </CardContent></Card>

          {/* Visual Breakdown Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Expense Pie Chart */}
            {Object.keys(pnl.expenses_by_category || {}).length > 0 && (
              <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-500" /> Expense Breakdown</h3>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={Object.entries(pnl.expenses_by_category).map(([k, v]) => ({ name: catLabel(k), value: v }))} cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2} dataKey="value">
                        {Object.entries(pnl.expenses_by_category).map((_, idx) => (<Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />))}
                      </Pie>
                      <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {Object.entries(pnl.expenses_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt], idx) => (
                    <span key={cat} className="text-xs flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                      {catLabel(cat)} ({pnl.total_expenses > 0 ? Math.round(amt / pnl.total_expenses * 100) : 0}%)
                    </span>
                  ))}
                </div>
              </CardContent></Card>
            )}
            
            {/* Expense Bar Chart */}
            {Object.keys(pnl.expenses_by_category || {}).length > 0 && (
              <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-stone-500" /> Expenses by Category</h3>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={Object.entries(pnl.expenses_by_category).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ name: catLabel(k), amount: v }))} layout="vertical" margin={{ left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Amount']} />
                      <Bar dataKey="amount" fill="#dc2626" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent></Card>
            )}
          </div>
          
          {/* Financing Activities */}
          {(pnl.financing?.total_in > 0 || pnl.financing?.total_out > 0) && (
            <Card className="bg-blue-50/30 border border-blue-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2"><Banknote className="w-4 h-4 text-blue-500" /> Financing Activities</h3>
                <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${pnl.financing.net >= 0 ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>Net: {fmt(pnl.financing.net)}</span>
              </div>
              <p className="text-xs text-stone-400 mb-4">Loans, investments, and dividends affect cash position but not operating profit.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.keys(pnl.financing.inflows || {}).length > 0 && (
                  <div className="bg-white/70 rounded-lg p-4">
                    <p className="text-xs font-semibold text-emerald-700 mb-2 uppercase tracking-wide">Inflows</p>
                    {Object.entries(pnl.financing.inflows).map(([cat, amt]) => (
                      <div key={cat} className="flex justify-between text-sm py-1.5 border-b border-stone-100 last:border-0"><span className="text-stone-600">{catLabel(cat)}</span><span className="text-emerald-700 font-medium">{fmt(amt)}</span></div>
                    ))}
                    <div className="flex justify-between text-sm py-1.5 mt-1 font-semibold"><span className="text-stone-700">Total</span><span className="text-emerald-700">{fmt(pnl.financing.total_in)}</span></div>
                  </div>
                )}
                {Object.keys(pnl.financing.outflows || {}).length > 0 && (
                  <div className="bg-white/70 rounded-lg p-4">
                    <p className="text-xs font-semibold text-red-600 mb-2 uppercase tracking-wide">Outflows</p>
                    {Object.entries(pnl.financing.outflows).map(([cat, amt]) => (
                      <div key={cat} className="flex justify-between text-sm py-1.5 border-b border-stone-100 last:border-0"><span className="text-stone-600">{catLabel(cat)}</span><span className="text-red-600 font-medium">{fmt(amt)}</span></div>
                    ))}
                    <div className="flex justify-between text-sm py-1.5 mt-1 font-semibold"><span className="text-stone-700">Total</span><span className="text-red-600">{fmt(pnl.financing.total_out)}</span></div>
                  </div>
                )}
              </div>
            </CardContent></Card>
          )}
          
          {/* Store breakdown */}
          {Object.keys(pnl.store_breakdown || {}).length > 1 && (
            <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
              <h3 className="text-sm font-semibold text-stone-700 mb-3">P&L by Store</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-stone-500 border-b border-stone-200">
                    <th className="text-left py-2 font-medium">Store</th><th className="text-right py-2 font-medium">Revenue</th><th className="text-right py-2 font-medium">Expenses</th><th className="text-right py-2 font-medium">Profit</th><th className="text-right py-2 font-medium">Margin</th>
                  </tr></thead>
                  <tbody>{Object.entries(pnl.store_breakdown).map(([sid, data]) => {
                    const profit = (data.income || 0) - (data.expenses || 0);
                    const margin = data.income > 0 ? Math.round(profit / data.income * 100) : 0;
                    return (
                      <tr key={sid} className="border-b border-stone-50 last:border-0 hover:bg-stone-50">
                        <td className="py-2.5 text-stone-700 font-medium">{sid === "general" ? "General" : getStoreName(sid)}</td>
                        <td className="py-2.5 text-right text-emerald-700">{fmt(data.income)}</td>
                        <td className="py-2.5 text-right text-red-600">{fmt(data.expenses)}</td>
                        <td className={`py-2.5 text-right font-semibold ${profit >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmt(profit)}</td>
                        <td className="py-2.5 text-right text-stone-500">{margin}%</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            </CardContent></Card>
          )}
            </>
          )}
        </div>
      )}

      {/* ===== CASH FLOW ===== */}
      {tab === "cashflow" && (
        <div className="space-y-4">
          {/* Controls: Quick months or custom date range */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-1.5">
              {["3", "6", "12"].map(m => (
                <button key={m} data-testid={`cf-${m}m`} onClick={() => { setCfMode("months"); setCashflowMonths(m); }} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${cfMode === "months" && cashflowMonths === m ? "bg-stone-900 text-stone-50" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>{m}mo</button>
              ))}
              <button data-testid="cf-custom" onClick={() => {
                setCfMode("range");
                if (!cfDateFrom) { const d = new Date(); d.setMonth(d.getMonth() - 3); setCfDateFrom(d.toISOString().slice(0,10)); }
                if (!cfDateTo) setCfDateTo(new Date().toISOString().slice(0,10));
              }} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${cfMode === "range" ? "bg-stone-900 text-stone-50" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>Custom</button>
            </div>
            {cfMode === "range" && (
              <div className="flex items-center gap-2">
                <Input data-testid="cf-from" type="date" value={cfDateFrom} onChange={e => setCfDateFrom(e.target.value)} className="w-40 h-8 text-xs" />
                <span className="text-xs text-stone-400">to</span>
                <Input data-testid="cf-to" type="date" value={cfDateTo} onChange={e => setCfDateTo(e.target.value)} className="w-40 h-8 text-xs" />
              </div>
            )}
          </div>

          {/* Summary Cards: Starting Cash, Cash In, Cash Out, Net Position */}
          {cashflow && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-stone-50 border-stone-200/60 rounded-xl" data-testid="cf-starting"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Starting Cash</p><p className="text-xl font-semibold text-stone-800">{fmt(cashflow.starting_cash)}</p></CardContent></Card>
                <Card className="bg-emerald-50/50 border-emerald-200/60 rounded-xl" data-testid="cf-total-in"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Cash In</p><p className="text-xl font-semibold text-emerald-800">{fmt(cashflow.total_inflows)}</p></CardContent></Card>
                <Card className="bg-red-50/50 border-red-200/60 rounded-xl" data-testid="cf-total-out"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Cash Out</p><p className="text-xl font-semibold text-red-700">{fmt(cashflow.total_outflows)}</p></CardContent></Card>
                <Card className={`rounded-xl ${(cashflow.net_position || 0) >= 0 ? "bg-emerald-50/50 border-emerald-200/60" : "bg-red-50/50 border-red-200/60"}`} data-testid="cf-total-net"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Net Position</p><p className={`text-xl font-semibold ${(cashflow.net_position || 0) >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmt(cashflow.net_position)}</p></CardContent></Card>
              </div>

              {/* Bar Chart */}
              <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
                <h3 className="text-sm font-semibold text-stone-700 mb-4">Monthly Cash Flow</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cashflow.monthly} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="inflows" fill="#059669" name="Inflows" radius={[4,4,0,0]} />
                      <Bar dataKey="outflows" fill="#dc2626" name="Outflows" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent></Card>

              {/* Monthly Breakdown Table */}
              <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
                <h3 className="text-sm font-semibold text-stone-700 mb-4">Monthly Breakdown</h3>
                <div className="space-y-3">
                  {cashflow.monthly.map(m => (
                    <div key={m.month} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-stone-600 w-20">{m.month}</span>
                      <div className="flex-1 flex gap-2 items-center">
                        <div className="flex-1 bg-stone-50 rounded-full h-5 relative overflow-hidden">
                          <div className="absolute left-0 top-0 h-full bg-emerald-400/60 rounded-full" style={{ width: `${Math.min(((m.inflows || 0) / Math.max(m.inflows || 0, m.outflows || 0, 1)) * 100, 100)}%` }} />
                        </div>
                        <span className="text-xs text-emerald-700 w-20 text-right">{fmt(m.inflows)}</span>
                      </div>
                      <div className="flex-1 flex gap-2 items-center">
                        <div className="flex-1 bg-stone-50 rounded-full h-5 relative overflow-hidden">
                          <div className="absolute left-0 top-0 h-full bg-red-400/60 rounded-full" style={{ width: `${Math.min(((m.outflows || 0) / Math.max(m.inflows || 0, m.outflows || 0, 1)) * 100, 100)}%` }} />
                        </div>
                        <span className="text-xs text-red-600 w-20 text-right">{fmt(m.outflows)}</span>
                      </div>
                      <span className={`text-xs font-medium w-20 text-right ${(m.net || 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(m.net)}</span>
                    </div>
                  ))}
                </div>
              </CardContent></Card>

              {/* Weekly Detail */}
              {cashflow?.weekly?.length > 0 && (
                <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-stone-700 mb-4">Weekly Cash Flow</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={cashflow.weekly}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                        <Area type="monotone" dataKey="net" stroke="#059669" fill="#d1fae5" name="Net" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent></Card>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== FORECAST ===== */}
      {tab === "forecast" && (
        <div className="space-y-4">
          <div className="flex gap-2 items-center">
            {["3", "6", "12"].map(m => (
              <button key={m} data-testid={`fc-${m}m`} onClick={() => setForecastMonths(m)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${forecastMonths === m ? "bg-stone-900 text-stone-50" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>{m} months</button>
            ))}
          </div>
          {forecast && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Card className="bg-white border border-stone-200/60 rounded-xl" data-testid="fc-current"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Current Cash</p><p className="text-2xl font-semibold text-stone-900">{fmt(forecast.current_cash)}</p></CardContent></Card>
                <Card className={`rounded-xl ${forecast.end_balance >= 0 ? "bg-emerald-50/50 border-emerald-200/60" : "bg-red-50/50 border-red-200/60"}`} data-testid="fc-end"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Projected ({forecastMonths}mo)</p><p className={`text-2xl font-semibold ${forecast.end_balance >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmt(forecast.end_balance)}</p></CardContent></Card>
                <Card className="bg-white border border-stone-200/60 rounded-xl"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Upcoming Payments</p><p className="text-2xl font-semibold text-stone-900">{forecast.upcoming_payments?.length || 0}</p></CardContent></Card>
              </div>

              {/* Cash Position Area Chart */}
              {forecast.forecast_weeks?.length > 0 && (
                <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-stone-700 mb-4">Projected Cash Position</h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={forecast.forecast_weeks}>
                        <defs>
                          <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#059669" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} labelFormatter={l => `Week of ${l}`} />
                        <Area type="monotone" dataKey="balance" stroke="#059669" fill="url(#cashGrad)" strokeWidth={2} name="Balance" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent></Card>
              )}

              {/* Upcoming Payments List */}
              {forecast.upcoming_payments?.length > 0 && (
                <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-stone-700 mb-3">Upcoming Scheduled Payments</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {forecast.upcoming_payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${_txn_is_inflow_fe(p.type) ? "bg-emerald-100" : "bg-red-100"}`}>
                            {_txn_is_inflow_fe(p.type) ? <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" /> : <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-stone-700">{p.description || catLabel(p.category)}</p>
                            <p className="text-xs text-stone-400">{p.date}</p>
                          </div>
                        </div>
                        <span className={`text-sm font-semibold ${_txn_is_inflow_fe(p.type) ? "text-emerald-700" : "text-red-600"}`}>{_txn_is_inflow_fe(p.type) ? "+" : "-"}{fmt(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent></Card>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== BURN RATE ===== */}
      {tab === "burn" && burnRate && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-white border border-stone-200/60 rounded-xl" data-testid="burn-weekly"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Weekly Burn</p><p className="text-2xl font-semibold text-red-600">{fmt(burnRate.weekly_burn)}</p></CardContent></Card>
            <Card className="bg-white border border-stone-200/60 rounded-xl" data-testid="burn-monthly"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Monthly Burn</p><p className="text-2xl font-semibold text-red-600">{fmt(burnRate.monthly_burn)}</p></CardContent></Card>
            <Card className="bg-white border border-stone-200/60 rounded-xl" data-testid="burn-cash"><CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Cash Position</p><p className="text-2xl font-semibold text-stone-900">{fmt(burnRate.current_cash)}</p></CardContent></Card>
            <Card className={`rounded-xl ${(burnRate.runway_months || 0) > 6 ? "bg-emerald-50/50 border-emerald-200/60" : "bg-amber-50/50 border-amber-200/60"}`} data-testid="burn-runway">
              <CardContent className="p-4"><p className="text-xs text-stone-500 mb-1">Runway</p><p className="text-2xl font-semibold text-stone-900">{burnRate.runway_months != null ? `${burnRate.runway_months}mo` : "N/A"}</p></CardContent>
            </Card>
          </div>
          {burnRate.weekly_expenses?.length > 0 && (
            <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]"><CardContent className="p-5">
              <h3 className="text-sm font-semibold text-stone-700 mb-4">Weekly Expenses (Last 12 Weeks)</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={burnRate.weekly_expenses}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Expenses']} labelFormatter={l => `Week of ${l}`} />
                    <Bar dataKey="amount" fill="#dc2626" radius={[4,4,0,0]} name="Expenses" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>
          )}
          {burnRate.weekly_burn === 0 && (
            <Card className="bg-emerald-50/30 border border-emerald-200/60 rounded-xl"><CardContent className="p-5 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-stone-800 mb-1">No Recent Burn</h3>
              <p className="text-sm text-stone-500">No operating expenses recorded in the last 12 weeks. Your runway is unlimited.</p>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* ===== CATEGORIES ===== */}
      {tab === "categories" && (
        <div className="max-w-3xl">
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Manage Categories
              </h2>
              <p className="text-sm text-stone-500 mb-6">Manage categories for your transactions. Deleting a category with connected items will make them uncategorized.</p>
              
              {/* Add new category */}
              <div className="flex gap-3 mb-6 p-4 bg-stone-50 rounded-lg">
                <Input
                  placeholder="New category name..."
                  value={newCatLabel}
                  onChange={e => setNewCatLabel(e.target.value)}
                  className="flex-1"
                />
                <Select value={newCatDirection} onValueChange={setNewCatDirection}>
                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="positive">Money In</SelectItem>
                    <SelectItem value="negative">Money Out</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={addCategory} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              {/* Category Lists */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Positive (Money In) */}
                <div>
                  <h3 className="text-sm font-medium text-emerald-700 mb-3 flex items-center gap-2">
                    <ArrowUpRight className="w-4 h-4" /> Money In Categories
                  </h3>
                  <div className="space-y-1">
                    {(categories.positive || []).map(cat => (
                      <div key={cat.value} className="flex items-center justify-between px-3 py-2 bg-emerald-50 rounded-lg group">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-stone-700">{cat.label}</span>
                          {cat.custom && <span className="text-[10px] text-stone-400 bg-stone-200/60 px-1.5 py-0.5 rounded">custom</span>}
                        </div>
                        <Button data-testid={`delete-cat-${cat.value}`} variant="ghost" size="sm" onClick={() => startDeleteCategory(cat.value, cat.label)} className="text-stone-300 hover:text-red-600 h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Negative (Money Out) */}
                <div>
                  <h3 className="text-sm font-medium text-red-700 mb-3 flex items-center gap-2">
                    <ArrowDownRight className="w-4 h-4" /> Money Out Categories
                  </h3>
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {(categories.negative || []).map(cat => (
                      <div key={cat.value} className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg group">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-stone-700">{cat.label}</span>
                          {cat.custom && <span className="text-[10px] text-stone-400 bg-stone-200/60 px-1.5 py-0.5 rounded">custom</span>}
                        </div>
                        <Button data-testid={`delete-cat-${cat.value}`} variant="ghost" size="sm" onClick={() => startDeleteCategory(cat.value, cat.label)} className="text-stone-300 hover:text-red-600 h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <SuggestedRulesCard categories={allCategories} onChange={() => { fetchRules(); fetchTransactions(); }} />
          <CategoryRulesCard rules={categoryRules} categories={allCategories} onChange={fetchRules} />
        </div>
      )}

      {/* Category Delete Confirmation Dialog */}
      <Dialog open={catDeleteDialog.open} onOpenChange={(open) => !open && setCatDeleteDialog({ open: false, value: null, label: "", usage: null, loading: false })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Delete Category
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-stone-700 mb-4">
              Are you sure you want to delete <span className="font-semibold">"{catDeleteDialog.label}"</span>?
            </p>
            {catDeleteDialog.loading ? (
              <div className="flex items-center gap-2 text-sm text-stone-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking connected items...
              </div>
            ) : catDeleteDialog.usage?.total > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-amber-800 mb-1">This category has connected items:</p>
                <ul className="text-sm text-amber-700 space-y-0.5">
                  {catDeleteDialog.usage.transactions > 0 && (
                    <li data-testid="delete-cat-txn-count">• {catDeleteDialog.usage.transactions} transaction{catDeleteDialog.usage.transactions !== 1 ? "s" : ""}</li>
                  )}
                  {catDeleteDialog.usage.future_payments > 0 && (
                    <li>• {catDeleteDialog.usage.future_payments} future payment{catDeleteDialog.usage.future_payments !== 1 ? "s" : ""}</li>
                  )}
                </ul>
                <p className="text-xs text-amber-600 mt-2">These items will become <span className="font-semibold">uncategorized</span>.</p>
              </div>
            ) : (
              <p className="text-sm text-stone-500 mb-4">No transactions or payments are linked to this category.</p>
            )}
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setCatDeleteDialog({ open: false, value: null, label: "", usage: null, loading: false })}>Cancel</Button>
              <Button
                data-testid="confirm-delete-cat-btn"
                variant="destructive"
                onClick={confirmDeleteCategory}
                disabled={catDeleteDialog.loading}
                className="bg-red-600 hover:bg-red-700"
              >
                {catDeleteDialog.loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Delete Category
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
